<script setup>
import { useColorMode, useEventListener, useTimeoutFn } from "@vueuse/core";
import { computed, ref } from "vue";

import { isNativePlatform } from "../capacitor/platform";
import { getServerUrl } from "../capacitor/server-url";

const mode = useColorMode({
  initialValue: "dark",
});

// The backend serves the company logo (or the default) at this path. On native
// the WebView origin is https://localhost, so a relative path would load the
// bundle's default logo instead of the server's. Prefix the configured server
// URL so the company logo loads cross-origin (the endpoint is a public GET).
const origin = ref("");
if (isNativePlatform()) {
  getServerUrl().then((url) => {
    if (url) origin.value = url.replace(/\/+$/, "");
  });
}

// The server marks the shell with each logo's version (web.ShellForLogo). A URL
// carrying the current one is served immutable, so a repeat load paints from
// the cache without a revalidation first, and an upload changes the URL. The
// native shell is bundled and carries none, so its logo revalidates.
const versions = document.documentElement.dataset;

const logoPath = computed(() => {
  const light = mode.value === "light";
  const path = light
    ? "/assets/images/Knocknoc_long_whitebg_Large_purple.png"
    : "/assets/images/Knocknoc_long_darkbg_Large_purple.png";
  const version = light ? versions.logoLight : versions.logoDark;
  return version ? `${path}?v=${encodeURIComponent(version)}` : path;
});
const logo = computed(() => origin.value + logoPath.value);

// A failed logo keeps its box and hides the alt text. It retries on coming back
// online and on a backoff capped at a minute, since a flaky connection may never
// go offline. Re-keying the element forces a fresh request.
const RETRY_FIRST_MS = 2000;
const RETRY_MAX_MS = 60000;

const failed = ref(false);
const attempt = ref(0);
const retryDelay = ref(RETRY_FIRST_MS);
const { start: scheduleRetry, stop: cancelRetry } = useTimeoutFn(retry, retryDelay, { immediate: false });

function retry() {
  cancelRetry();
  attempt.value++;
}

function onError() {
  failed.value = true;
  scheduleRetry();
  retryDelay.value = Math.min(retryDelay.value * 2, RETRY_MAX_MS);
}

function onLoad() {
  failed.value = false;
  retryDelay.value = RETRY_FIRST_MS;
}

useEventListener(window, "online", () => {
  if (failed.value) retry();
});
</script>

<template>
  <img
    :key="attempt"
    :src="logo"
    alt="Knocknoc logo"
    width="1521"
    height="471"
    :class="{ invisible: failed }"
    @error="onError"
    @load="onLoad"
  />
</template>
